import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { votePurchaseService } from "../../services/votePurchaseService";

const VotePurchaseModal = ({
  isOpen,
  package: pkg,
  onClose,
  onSuccess,
}) => {
  const [paymentDetails, setPaymentDetails] = useState({
    phone: "",
    operator: "mtn",
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("idle"); // idle, processing, verifying, success, error
  const [referenceId, setReferenceId] = useState(null);
  const [purchaseId, setPurchaseId] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [errors, setErrors] = useState({});

  const OPERATORS = [
    { value: "mtn", label: "MTN" },
    { value: "airtel", label: "Airtel" },
    { value: "zamtel", label: "Zamtel" },
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!paymentDetails.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(paymentDetails.phone.replace(/\D/g, ""))) {
      newErrors.phone = "Phone number must be 10 digits";
    }

    if (!paymentDetails.operator) {
      newErrors.operator = "Please select a mobile operator";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPaymentDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const verifyPaymentStatus = async (refId, pId, count = 0) => {
    try {
      if (count > 60) {
        // Stop after 5 minutes (60 polls of 5 seconds each)
        setPaymentStatus("error");
        setIsProcessing(false);
        toast.error("Payment verification timeout. Please try again.");
        return;
      }

      const verifyResponse = await votePurchaseService.verifyVotePayment(refId);

      if (
        verifyResponse.status === true &&
        verifyResponse.data.status === "successful"
      ) {
        // ✅ Payment succeeded - Now complete purchase on backend
        setPaymentStatus("success");
        
        try {
          await votePurchaseService.completeVotePurchase(refId, {
            purchaseId: pId,
            paymentConfirmed: true,
            packageId: pkg.packageId,
            votes: pkg.votes,
            votesPerDay: pkg.votesPerDay,
            durationDays: pkg.durationDays,
            type: pkg.type,
            status: "active"
          });

          votePurchaseService.clearPendingVotePayment();
          setIsProcessing(false);

          toast.success(`${pkg.name} - Votes added successfully!`);
          
          setTimeout(() => {
            onSuccess();
            handleClose();
          }, 2000);
        } catch (completeError) {
          console.error("Error completing purchase on backend:", completeError);
          const errorMsg = completeError?.response?.data?.message || 
                          "Failed to add votes to your account";
          setPaymentStatus("error");
          setIsProcessing(false);
          toast.error(errorMsg);
        }
      } else if (verifyResponse.data.status === "failed") {
        setPaymentStatus("error");
        votePurchaseService.clearPendingVotePayment();
        setIsProcessing(false);
        toast.error("Payment failed. Please try again.");
      } else {
        // Continue polling - payment still pending
        setPaymentStatus("verifying");
        setPollCount(count + 1);
        setTimeout(() => verifyPaymentStatus(refId, pId, count + 1), 5000);
      }
    } catch (error) {
      console.error("Error verifying payment:", error);
      // Continue polling on error
      setTimeout(() => verifyPaymentStatus(refId, pId, count + 1), 5000);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsProcessing(true);
      setPaymentStatus("processing");

      // Initiate payment
      const result = await votePurchaseService.initiateVotePurchase(pkg.packageId, {
        phone: paymentDetails.phone,
        operator: paymentDetails.operator,
        amount: pkg.price,
      });

      votePurchaseService.setPendingVotePayment({
        referenceId: result.referenceId,
        purchaseId: result.purchaseId,
        packageData: {
          packageId: pkg.packageId,
          votes: pkg.votes,
          votesPerDay: pkg.votesPerDay,
          durationDays: pkg.durationDays,
          type: pkg.type,
        },
        packageId: pkg.packageId,
        packageName: pkg.name,
        amount: pkg.price,
        active: false,
        createdAt: new Date().toISOString(),
      });

      setReferenceId(result.referenceId);
      setPurchaseId(result.purchaseId);
      toast.info("Payment initiated. Waiting for confirmation...");

      // Start verification polling - pass purchaseId directly to avoid async state issues
      verifyPaymentStatus(result.referenceId, result.purchaseId, 0);
    } catch (error) {
      console.error("Error processing payment:", error);
      setPaymentStatus("error");
      const errorMessage =
        error.response?.data?.message || "Failed to process payment";
      toast.error(errorMessage);
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (paymentStatus !== "processing" && paymentStatus !== "verifying") {
      setPaymentDetails({ phone: "", operator: "mtn" });
      setErrors({});
      setPaymentStatus("idle");
      setReferenceId(null);
      setPurchaseId(null);
      setPollCount(0);
      onClose();
    }
  };

  if (!isOpen || !pkg) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-800 rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto border border-line">
        {/* Header */}
        <div className="bg-surface-850 p-6 border-b border-line">
          <h2 className="text-xl font-bold text-white">Confirm Purchase</h2>
          <p className="text-gray-400 text-sm mt-1">Complete your vote package purchase</p>
          {referenceId && (
            <p className="text-xs text-gray-500 mt-2 font-mono">Ref: {referenceId}</p>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Order Summary */}
          <div className="bg-surface-900 rounded-lg p-4 mb-6 border border-line">
            <h3 className="text-sm font-semibold text-gray-300 mb-3">
              Order Summary
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Package:</span>
                <span className="text-white font-medium">{pkg.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Type:</span>
                <span className={`font-medium ${pkg.type === "daily" ? "text-white" : "text-accent-soft"}`}>
                  {pkg.type === "daily" ? "Daily" : "Weekly"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Votes:</span>
                <span className="text-green-400 font-medium">
                  {pkg.type === "daily"
                    ? `${(pkg.votes || 0).toLocaleString()}`
                    : `${(pkg.votesPerDay || 0).toLocaleString()}/day`}
                </span>
              </div>
              <div className="border-t border-line my-3 pt-3 flex justify-between font-bold">
                <span className="text-gray-300">Amount:</span>
                <span className="text-white text-lg">
                  ZMW {pkg.price.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          {paymentStatus === "idle" || paymentStatus === "error" ? (
            <form onSubmit={handlePaymentSubmit} className="space-y-4">
              {/* Operator Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Mobile Operator *
                </label>
                <select
                  name="operator"
                  value={paymentDetails.operator}
                  onChange={handlePaymentChange}
                  className={`field ${
                    errors.operator ? "border-accent" : ""
                  }`}
                >
                  <option value="">Select Operator</option>
                  {OPERATORS.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>
                {errors.operator && (
                  <p className="text-red-500 text-xs mt-1">{errors.operator}</p>
                )}
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Phone Number (10 digits) *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={paymentDetails.phone}
                  onChange={handlePaymentChange}
                  placeholder="e.g., 0961234567"
                  maxLength="10"
                className={`field ${
                  errors.phone ? "border-accent" : ""
                }`}
                />
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                )}
              </div>

              {/* Info Box */}
              <div className="bg-surface-900 border border-line rounded-lg p-3 text-xs text-gray-400">
                <p className="font-semibold mb-1 text-white">Payment via {paymentDetails.operator.toUpperCase()}</p>
                <p>You will receive a prompt to confirm payment on your phone.</p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6 pt-4 border-t border-line">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isProcessing}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isProcessing}
                  className="btn-primary flex-1"
                >
                  {isProcessing ? "Processing..." : "Proceed to Payment"}
                </button>
              </div>
            </form>
          ) : paymentStatus === "processing" || paymentStatus === "verifying" ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="mb-4">
                <div className="w-12 h-12 border-4 border-surface-600 border-t-accent rounded-full animate-spin mx-auto"></div>
              </div>
              <p className="text-gray-300 font-semibold mb-2">
                {paymentStatus === "processing" ? "Processing Payment..." : "Verifying Payment..."}
              </p>
              <p className="text-gray-500 text-sm text-center">
                {paymentStatus === "processing"
                  ? "Sending payment request to your phone"
                  : "Waiting for payment confirmation"}
              </p>
              <p className="text-gray-600 text-xs mt-3">
                Poll {pollCount}/60 • Timeout in ~{Math.floor((60 - pollCount) * 5 / 60)} min
              </p>
            </div>
          ) : paymentStatus === "success" ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="mb-4">
                <svg
                  className="w-16 h-16 text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p className="text-white font-bold text-lg mb-2">Payment Successful!</p>
              <p className="text-green-400 text-sm mb-4">
                Your votes have been added to your account
              </p>
              <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-3 w-full text-sm text-green-200 text-center">
                +{pkg.type === "daily" ? pkg.votes : pkg.votesPerDay} votes
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <svg
                className="w-12 h-12 text-red-400 mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-white font-bold mb-2">Payment Failed</p>
              <p className="text-gray-400 text-sm text-center mb-4">
                Something went wrong. Please try again.
              </p>
              <button
                onClick={handleClose}
                className="btn-secondary px-6 py-2"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VotePurchaseModal;
